import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { PrimeNGConfig } from 'primeng/api';
import { Store } from '@ngrx/store';
import { combineLatest, filter, first, Observable, takeUntil } from 'rxjs';
import { StoreModel } from './shared/interfaces/store-interfaces';
import * as Sentry from '@sentry/angular';
import { loadStores, selectStore } from './shared/state/store/store-actions';
import { selectAllStores, selectSelectedStore } from './shared/state/store/store-selectors';
import { selectIsAuthenticated, selectRoles } from './shared/state/auth/auth.selectors';
import { selectSubscriptionLoaded } from './shared/state/subscription/subscription.selectors';
import { loadAllTicketCategories } from './shared/state/ticket-categories/ticket-categories.actions';
import { Capacitor } from '@capacitor/core';
import { FcmService } from './shared/services/fcm.service';
import { Platform } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { GoogleAnalyticsService } from './shared/services/google-analytics.service';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  stores$: Observable<StoreModel[]>;
  selectedStore$: Observable<StoreModel | null>;

  constructor(
    private primengConfig: PrimeNGConfig,
    private store: Store,
    private cd: ChangeDetectorRef,
    private fcmService: FcmService,
    private platform: Platform,
    private googleAnalytics: GoogleAnalyticsService // Initialize Google Analytics (loads based on environment)
  ) {
    this.stores$ = this.store.select(selectAllStores); // Subscribe to stores
    this.selectedStore$ = this.store.select(selectSelectedStore); // Subscribe to selectedStore

    (window as any)['appComponent'] = this;
  }

  ngOnInit(): void {
    this.primengConfig.ripple = true;

    // Load Google Maps script dynamically based on platform
    this.loadGoogleMapsScript();

    // Add platform-specific CSS class
    this.addPlatformClass();

    // Wait for both authentication and subscription to be loaded before initial loads
    combineLatest([this.store.select(selectIsAuthenticated), this.store.select(selectSubscriptionLoaded)])
      .pipe(
        filter(([isAuthenticated, subscriptionLoaded]) => isAuthenticated && subscriptionLoaded) // Wait for both
      )
      .subscribe(() => {
        console.log('User is authenticated and subscription is loaded, loading stores...');
        this.store.dispatch(loadStores()); // Dispatch store loading
        this.store.dispatch(loadAllTicketCategories()); // Dispatch ticket categories loading

        // Only register FCM token if Capacitor SDK is available (native platform)
        if (Capacitor.isNativePlatform()) {
          this.fcmService.register().subscribe({
            next: (response) => {
              console.log('FCM token registered:', response);
            },
            error: (error) => {
              console.error('FCM registration failed:', error.message);
            },
          });
        } else {
          console.log('FCM registration skipped - not on native platform (web browser)');
        }
      });

    combineLatest([this.store.select(selectAllStores), this.store.select(selectSelectedStore)]).subscribe(([stores, selectedStore]) => {
      if (!selectedStore && stores.length > 0) {
        this.store.dispatch(selectStore({ store: stores[0] }));
        console.log('[APP COMPONENT] Store initialized:', stores[0]);
      }
    });
  }

  private addPlatformClass(): void {
    const platform = Capacitor.getPlatform();
    document.body.classList.add(platform);
    console.log(`Platform detected: ${platform}`);
  }

  /**
   * Load Google Maps API using the official @googlemaps/js-api-loader v2.0+
   * - Web + localhost: Uses dev_google_places_key
   * - Android: Uses android_google_places_key
   * - iOS: Uses ios_google_places_key
   * - Web (production): Uses browser_google_places_key
   */
  private loadGoogleMapsScript(): void {
    const platform = Capacitor.getPlatform();
    const isLocalhost = window.location.origin.includes('localhost') || window.location.hostname === 'localhost';
    let apiKey: string;

    // Use dev key when on web platform AND running on localhost
    if (platform === 'web' && isLocalhost) {
      apiKey = environment.dev_google_places_key;
      console.log('[App Component] Loading Google Maps with DEV API key (localhost)');
    } else {
      // Production environment - use platform-specific keys
      switch (platform) {
        case 'android':
          apiKey = environment.android_google_places_key;
          console.log('[App Component] Loading Google Maps with Android API key');
          break;
        case 'ios':
          apiKey = environment.ios_google_places_key;
          console.log('[App Component] Loading Google Maps with iOS API key');
          break;
        case 'web':
        default:
          apiKey = environment.browser_google_places_key;
          console.log('[App Component] Loading Google Maps with Browser/Web API key');
          break;
      }
    }

    // Configure the Google Maps API loader (v2.0+ API)
    setOptions({
      key: apiKey,
      version: 'weekly',
    } as any);

    // Preload the places library
    importLibrary('places')
      .then(() => {
        console.log('[App Component] Google Maps Places API loaded successfully');
      })
      .catch((error: any) => {
        console.error('[App Component] Error loading Google Maps API:', error);
      });
  }
}
